import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavParams, ViewController } from "ionic-angular";
import moment from "moment";
import {
  CareSummaryDetail,
  CareSummaryStatusResult,
  PatientPreVisitSummaryService,
} from "providers/patient-charts/patient-pre-visit-summary-service";
import { Subject } from "rxjs";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { interval } from "rxjs/observable/interval";
import { merge } from "rxjs/observable/merge";
import { of } from "rxjs/observable/of";
import {
  catchError,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from "rxjs/operators";

@Component({
  selector: "patient-summary",
  templateUrl: "patient-summary.html",
})
export class PatientSummary implements OnInit, OnDestroy {
  patientSummaryData: CareSummaryDetail | null = null;
  summaryLoading: boolean = false;
  patientId: string;
  patientChartId: number;
  isPlaying: boolean = false;
  audioLoading: boolean = false;
  private audio = new Audio();
  private audioUrl: string | null = null;
  generateFileError: string = "";
  rating: string = "";
  patientExternalID: number;

  // Async polling state
  summaryStatus: string | null = null;
  lastSyncedAt: string | null = null;
  isPolling: boolean = false;
  isSyncing: boolean = false;
  elapsedTime: number = 0;

  // CO-1488 — multi-document summary sections.
  // Murphi returns a JSON object with one entry per source document section
  // (e.g. summary, recommendations, riskFactors, medications). The backend
  // forwards summaryText as a string; if the string parses to an object we
  // render each top-level field as its own labeled section, otherwise we
  // render it as a single "Summary" section.
  summarySections: { label: string; text: string }[] = [];

  private startTime: number = 0;
  private destroy$ = new Subject<void>();
  private forceRefresh$ = new BehaviorSubject<boolean>(false);
  private readonly MAX_WAIT_TIME = 10 * 60 * 1000; // 10 minutes
  private readonly POLL_INTERVAL = 10000; // 10 seconds
  private readonly STALE_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours

  constructor(
    public navParams: NavParams,
    public viewCtrl: ViewController,
    private careSummaryService: PatientPreVisitSummaryService
  ) {
    this.patientId = navParams.get("patientId");
    this.patientExternalID = navParams.get("patientExternalID");
    this.patientChartId =
      typeof this.patientId === "string"
        ? parseInt(this.patientId, 10)
        : this.patientId;

    this.audio = new Audio();
    this.audio.preload = "auto";
    this.audio.addEventListener("ended", () => {
      this.isPlaying = false;
    });
    this.audio.addEventListener("error", () => {
      this.isPlaying = false;
    });
  }

  ngOnInit() {
    this.checkStatusAndLoad();
  }

  /**
   * Step 1: Check current care summary status for this patient.
   * - COMPLETED + recent (<24h) → load existing summary
   * - PENDING → start polling
   * - null/FAILED/stale → trigger new generation
   */
  checkStatusAndLoad() {
    this.summaryLoading = true;
    this.generateFileError = "";

    console.log(
      `[CARE-SUMMARY-FE] Modal opened — checking status patient=${this.patientChartId}`
    );

    this.careSummaryService.getCareSummaryStatus(this.patientChartId).subscribe(
      (result: CareSummaryStatusResult) => {
        this.summaryStatus = result.status;
        this.lastSyncedAt = result.last_synced_at;

        console.log(
          `[CARE-SUMMARY-FE] Status check result patient=${
            this.patientChartId
          } status=${result.status} lastSyncedAt=${
            result.last_synced_at
          } isStale=${this.isStaleSummary()}`
        );

        if (result.status === "COMPLETED" && !this.isStaleSummary()) {
          this.loadExistingSummary();
        } else if (result.status === "PENDING") {
          this.startPollingForStatus();
        } else {
          this.triggerGeneration();
        }
      },
      (err) => {
        // No existing summary or backend error — trigger fresh generation.
        // Logged at warn so transient backend issues during the initial check
        // are visible in browser console without obscuring real errors.
        console.warn(
          `[CARE-SUMMARY-FE] Status check failed for patient=${this.patientChartId} — falling back to triggerGeneration`,
          err
        );
        this.triggerGeneration();
      }
    );
  }

  /**
   * Step 2a: Load existing completed summary from backend.
   */
  loadExistingSummary() {
    this.careSummaryService.getCareSummaries(this.patientChartId).subscribe(
      (summaries: CareSummaryDetail[]) => {
        if (summaries && summaries.length > 0) {
          this.patientSummaryData = summaries[0]; // most recent
          this.audioUrl = null; // re-presign for the freshly loaded summary
          this.summaryStatus = "COMPLETED";
          this.summarySections = this.buildSummarySections(
            this.patientSummaryData.summaryText
          );
          console.log(
            `[CARE-SUMMARY-FE] Loaded summary patient=${this.patientChartId} sections=${this.summarySections.length}`
          );
        }
        this.summaryLoading = false;
      },
      () => {
        this.generateFileError =
          "Failed to load care summary. Please try again.";
        this.summaryLoading = false;
      }
    );
  }

  /**
   * CO-1488 — merge multiple-document Murphi response into renderable sections.
   *
   * If summaryText parses to a JSON object, each top-level key becomes a labeled
   * section (camelCase keys are humanized to Title Case). Strings that don't
   * parse (or aren't objects) get rendered as a single "Summary" section.
   * Empty/null produces an empty array so the template hides the summary block.
   */
  private buildSummarySections(
    summaryText: string | null | undefined
  ): { label: string; text: string }[] {
    if (!summaryText) return [];

    const trimmed = summaryText.trim();

    // Cheap heuristic: only attempt JSON.parse if it looks like an object.
    // Avoids false positives on plain prose that happens to contain a brace.
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const sections = Object.keys(parsed)
            .filter((key) => parsed[key] != null && parsed[key] !== "")
            .map((key) => ({
              label: this.humanizeKey(key),
              text: this.coerceToString(parsed[key]),
            }))
            .filter((section) => section.text.length > 0);

          if (sections.length > 0) {
            return sections;
          }
          // Object parsed but had no usable string fields — fall through to
          // rendering the raw text so we at least show something.
        }
      } catch (e) {
        // Not JSON — fall through to plain text.
      }
    }

    return [{ label: "Summary", text: trimmed }];
  }

  /**
   * "summaryText" -> "Summary Text", "riskFactors" -> "Risk Factors",
   * "general_pre_idg_patient_summary" -> "General Pre Idg Patient Summary".
   */
  private humanizeKey(key: string): string {
    return key
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  /**
   * Murphi can return nested objects/arrays for some sections. Coerce to a
   * displayable string without losing structure (pretty-printed JSON for
   * non-strings, trimmed strings).
   */
  private coerceToString(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  /**
   * Step 2b: Trigger async generation via POST /care-summary/generate.
   */
  triggerGeneration() {
    this.summaryLoading = true;
    this.generateFileError = "";

    console.log(
      `[CARE-SUMMARY-FE] Triggering generation patient=${this.patientChartId}`
    );

    this.careSummaryService.generateCareSummary(this.patientChartId).subscribe(
      (result) => {
        this.summaryStatus = "PENDING";
        console.log(
          `[CARE-SUMMARY-FE] Generation accepted patient=${this.patientChartId} careSummary=${result?.careSummaryId} — starting polling`
        );
        this.startPollingForStatus();
      },
      (error) => {
        this.summaryLoading = false;
        this.generateFileError =
          "Failed to start care summary generation. Please try again.";
        console.error(
          `[CARE-SUMMARY-FE] Generation request failed patient=${this.patientChartId}`,
          error
        );
      }
    );
  }

  /**
   * Step 3: Poll GET /care-summary/status/{patientChartId} every 10s.
   * Stops when COMPLETED, FAILED, or max wait time exceeded.
   */
  startPollingForStatus() {
    this.summaryLoading = true;
    this.isPolling = true;
    this.startTime = Date.now();

    const automaticPoll$ = interval(this.POLL_INTERVAL);

    merge(automaticPoll$, this.forceRefresh$)
      .pipe(
        takeWhile(() => this.shouldContinuePolling()),
        takeUntil(this.destroy$),
        tap(() => {
          this.isSyncing = true;
          this.elapsedTime = Date.now() - this.startTime;
        }),
        switchMap(() =>
          this.careSummaryService
            .getCareSummaryStatus(this.patientChartId)
            .pipe(
              catchError(() => {
                this.isSyncing = false;
                return of(null);
              })
            )
        )
      )
      .subscribe({
        next: (result: CareSummaryStatusResult | null) => {
          this.isSyncing = false;
          if (!result) return;

          this.summaryStatus = result.status;
          this.lastSyncedAt = result.last_synced_at;

          if (result.status === "COMPLETED") {
            console.log(
              `[CARE-SUMMARY-FE] Polling saw COMPLETED patient=${this.patientChartId} elapsedMs=${this.elapsedTime} — loading summary`
            );
            this.stopPolling();
            this.loadExistingSummary();
          } else if (result.status === "FAILED") {
            console.warn(
              `[CARE-SUMMARY-FE] Polling saw FAILED patient=${this.patientChartId} elapsedMs=${this.elapsedTime}`
            );
            this.stopPolling();
            this.summaryLoading = false;
            this.generateFileError =
              "Care summary generation failed. Please try again.";
          }
        },
        complete: () => {
          this.isPolling = false;
        },
      });
  }

  shouldContinuePolling(): boolean {
    const elapsed = Date.now() - this.startTime;
    if (elapsed >= this.MAX_WAIT_TIME || !this.isPolling) {
      if (elapsed >= this.MAX_WAIT_TIME) {
        this.generateFileError = "Maximum wait time reached. Please try again.";
        this.summaryLoading = false;
      }
      return false;
    }
    return true;
  }

  stopPolling() {
    this.isPolling = false;
  }

  isStaleSummary(): boolean {
    if (!this.lastSyncedAt) return true;
    // moment.utc parses both "YYYY-MM-DD HH:mm:ss+00" (Postgres) and
    // "YYYY-MM-DDTHH:mm:ss+00:00" (ISO 8601). Using .utc preserves the
    // offset so the diff is in absolute time, not local clock time.
    const syncedAt = moment.utc(this.lastSyncedAt);
    if (!syncedAt.isValid()) return true;
    return moment.utc().diff(syncedAt) > this.STALE_THRESHOLD;
  }

  /**
   * Manual refresh — triggers new generation regardless of current status.
   */
  refreshSummary() {
    console.log(
      `[CARE-SUMMARY-FE] Manual refresh clicked patient=${this.patientChartId}`
    );
    this.patientSummaryData = null;
    this.summarySections = [];
    this.generateFileError = "";
    this.stopPolling();
    this.triggerGeneration();
  }

  /**
   * Force a status check during active polling.
   */
  syncStatus() {
    this.forceRefresh$.next(true);
  }

  getFormattedSyncTime(): string {
    if (!this.lastSyncedAt) return "";
    // Parse as UTC (backend returns Postgres or ISO 8601 with offset) then
    // convert to the user's local zone for display.
    const syncedAt = moment.utc(this.lastSyncedAt).local();
    if (!syncedAt.isValid()) return "";
    return syncedAt.format("MMM D, YYYY h:mm A");
  }

  getRating(): number | null {
    switch (this.rating) {
      case "very":
        return 3;
      case "somewhat":
        return 2;
      case "not-at-all":
        return 1;
      default:
        return null;
    }
  }

  closeModal() {
    this.viewCtrl.dismiss({ cancel: true });
  }

  handleChange(event: any): void {
    this.rating = event;
  }

  playAudio() {
    if (!this.patientSummaryData?.audioS3Key) return;

    if (this.isPlaying) {
      this.pauseAudio();
      return;
    }

    // audioS3Key is a raw s3:// key the browser can't play. Fetch a presigned URL
    // once, then play; reuse it on replay.
    if (this.audioUrl) {
      this.startAudio(this.audioUrl);
      return;
    }

    const s3Key = this.toS3ObjectKey(this.patientSummaryData.audioS3Key);
    this.audioLoading = true;
    this.careSummaryService
      .getPresignedUrl(s3Key)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.audioLoading = false;
          this.audioUrl = res.url;
          this.startAudio(res.url);
        },
        () => {
          this.audioLoading = false;
          this.generateFileError =
            "Unable to play the narrative audio. Please try again.";
        }
      );
  }

  private startAudio(url: string) {
    if (this.audio.src !== url) {
      this.audio.src = url;
    }
    this.audio
      .play()
      .then(() => {
        this.isPlaying = true;
      })
      .catch(() => {
        this.generateFileError =
          "Unable to play the narrative audio. Please try again.";
      });
  }

  // audioS3Key is stored as "s3://<bucket>/<key>"; the presigned-url endpoint wants
  // just <key>. A plain key is returned unchanged.
  private toS3ObjectKey(uri: string): string {
    const match = /^s3:\/\/[^/]+\/(.+)$/.exec(uri);
    return match ? match[1] : uri;
  }

  pauseAudio() {
    this.audio.pause();
    this.isPlaying = false;
  }

  ngOnDestroy() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.stopPolling();
    this.forceRefresh$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/patient-summary/patient-summary.ts